import { ProBadge } from "@/components/ProBadge";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CaretDown, Sparkle, TrendUp, TrendDown, CalendarCheck, Timer, Flame, Target, CheckCircle, ChatTeardropText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PILLAR_META } from "./meta";
import { ProLock } from "@/components/ProGate";

export default function HistoryTab() {
  const [review, setReview] = useState(null);
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/history").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
    api.get("/weekly-review")
      .then(({ data }) => setReview(data))
      .catch((e) => setReview(e?.response?.status === 403 ? { locked: true } : { has_data: false }));
  }, []);

  return (
    <div className="space-y-6" data-testid="history-tab">
      <WeeklyReviewCard review={review} />
      <div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="label-eyebrow">Plan history — last 30 days</span>
          <MonthAdherence items={items} />
        </div>
        {items === null ? (
          <div className="clay p-8 mt-3 text-center text-sm text-muted2">Loading your history…</div>
        ) : items.length === 0 ? (
          <div className="clay p-10 mt-3 text-center" data-testid="history-empty">
            <p className="font-display text-xl font-light">No history yet</p>
            <p className="text-sm text-muted2 mt-2">Every daily check-in adds a day here — your training story, one optimal day at a time.</p>
          </div>
        ) : (
          <div className="space-y-3 mt-3">
            {items.map((it, i) => <HistoryItem key={it.date} item={it} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function MonthAdherence({ items }) {
  if (!items?.length) return null;
  const fb = items.map((i) => i.feedback).filter(Boolean);
  if (!fb.length) return null;
  const done = fb.filter((f) => f === "done").length;
  const partial = fb.filter((f) => f === "partial").length;
  return (
    <span data-testid="month-adherence" className="inline-flex items-center gap-1.5 text-xs font-medium bg-sage/10 text-sage rounded-full px-3 py-1.5">
      <CheckCircle size={13} weight="fill" />
      {done + partial} of {fb.length} plans completed this month
    </span>
  );
}

const FEEDBACK_BADGE = {
  done: { label: "Did it", color: "var(--sage)", bg: "rgba(22,188,156,0.12)" },
  partial: { label: "Partly", color: "#E8A54B", bg: "rgba(232,165,75,0.15)" },
  skipped: { label: "Skipped", color: "#7C8AA5", bg: "rgba(124,138,165,0.10)" },
};

function WeeklyReviewCard({ review }) {
  if (review === null) {
    return (
      <div className="clay p-8 flex items-center gap-3" data-testid="weekly-review-loading">
        <div className="w-5 h-5 rounded-full border-2 border-brand border-t-transparent animate-spin" />
        <span className="text-sm text-muted2">Your coach is writing this week's review…</span>
      </div>
    );
  }
  if (review.locked) {
    return <ProLock testId="weekly-locked" feature="Weekly AI Review" desc="Your coach writes a personal review of every week — stats, wins and next week's focus. Your 7-day Pro trial has ended." />;
  }
  if (!review.has_data) {
    return (
      <div className="clay p-8" data-testid="weekly-review-empty">
        <span className="label-eyebrow">Weekly review</span>
        <p className="text-sm text-muted2 mt-2">Check in a few days this week and your coach will write your first weekly review.</p>
      </div>
    );
  }
  const best = review.best_pillar && PILLAR_META[review.best_pillar.key];
  const worst = review.worst_pillar && PILLAR_META[review.worst_pillar.key];
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="clay p-8" data-testid="weekly-review-card">
      <div className="flex items-center gap-2">
        <Sparkle size={16} weight="fill" color="var(--brand)" />
        <span className="label-eyebrow flex items-center gap-2">Your week in review <ProBadge testId="pro-badge-weekly" /></span>
      </div>
      <h2 className="font-display font-light tracking-tight text-2xl sm:text-3xl mt-3" data-testid="weekly-headline">
        {review.ai?.headline}
      </h2>
      <p className="text-[15px] text-ink/80 leading-relaxed mt-2 max-w-2xl" data-testid="weekly-summary">{review.ai?.summary}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <StatTile label="Avg Healthspan" value={review.avg_score} sub={review.delta != null ? `${review.delta >= 0 ? "+" : ""}${review.delta} vs last week` : "first week"}
          icon={review.delta >= 0 ? TrendUp : TrendDown} color={review.delta == null || review.delta >= 0 ? "var(--sage)" : "#EF6A56"} testId="weekly-avg-score" />
        <StatTile label="Check-ins" value={review.checkins} sub="this week" icon={CalendarCheck} color="var(--brand)" testId="weekly-checkins" />
        <StatTile label="Days added" value={`+${review.days_added}`} sub="to your healthspan" icon={Flame} color="#E8A54B" testId="weekly-days-added" />
        {best && (
          <StatTile label="Strongest pillar" value={best.label} sub={`${review.best_pillar.score}/100`} icon={best.icon} color={best.color} testId="weekly-best-pillar" />
        )}
        {review.plan_adherence && (
          <StatTile label="Plans completed" value={review.plan_adherence.done}
            sub={review.plan_adherence.partial ? `+${review.plan_adherence.partial} partly` : "this week"}
            icon={CheckCircle} color="var(--sage)" testId="weekly-plans-completed" />
        )}
      </div>

      {review.ai?.focus && (
        <div className="mt-5 rounded-2xl bg-brand/8 border border-brand/15 px-4 py-3 flex items-start gap-2.5" data-testid="weekly-focus">
          <Target size={17} weight="fill" color="var(--brand)" className="mt-0.5 shrink-0" />
          <div>
            <span className="text-xs font-semibold text-brand uppercase tracking-wide">Next week's focus{worst ? ` — ${worst.label}` : ""}</span>
            <p className="text-sm text-ink/85 mt-0.5 leading-relaxed">{review.ai.focus}</p>
          </div>
        </div>
      )}

      {review.ai?.reflection_question && <ReflectionBox review={review} />}
    </motion.div>
  );
}

function ReflectionBox({ review }) {
  const [answer, setAnswer] = useState("");
  const [saved, setSaved] = useState(review.reflection_answer || null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  const save = async () => {
    if (!answer.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/weekly-review/reflect", { answer: answer.trim() });
      setSaved(data.reflection_answer);
      setEditing(false);
    } catch {
      toast.error("Could not save your reflection");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl bg-secondary/60 px-4 py-4" data-testid="weekly-reflection">
      <div className="flex items-start gap-2.5">
        <ChatTeardropText size={17} weight="fill" color="var(--sleep)" className="mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--sleep)" }}>A moment to reflect</span>
          <p className="text-sm text-ink/85 mt-0.5 leading-relaxed" data-testid="reflection-question">{review.ai.reflection_question}</p>
          {saved && !editing ? (
            <div className="mt-3" data-testid="reflection-answer">
              <p className="text-sm text-muted2 italic leading-relaxed">"{saved}"</p>
              <button data-testid="reflection-edit-btn" onClick={() => { setAnswer(saved); setEditing(true); }}
                className="text-xs text-brand hover:underline font-medium mt-1.5">Edit reflection</button>
            </div>
          ) : (
            <div className="mt-3">
              <textarea
                data-testid="reflection-input"
                rows={2}
                maxLength={1000}
                className="w-full bg-surface border border-border rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand transition-colors"
                placeholder="A sentence or two — your coach remembers these week to week…"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
              />
              <button
                data-testid="reflection-save-btn"
                onClick={save}
                disabled={saving || !answer.trim()}
                className="mt-2 bg-ink text-bg rounded-full px-5 py-2 text-xs font-medium hover:bg-brand transition-colors disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save reflection"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, sub, icon: Icon, color, testId }) {
  return (
    <div className="rounded-2xl bg-secondary/60 p-4" data-testid={testId}>
      <Icon size={17} weight="fill" color={color} />
      <div className="font-display text-2xl font-light tracking-tight mt-1.5">{value}</div>
      <div className="text-xs text-muted2">{label}</div>
      <div className="text-[11px] text-muted2/80 mt-0.5">{sub}</div>
    </div>
  );
}

const dateLabel = (d) => new Date(d + "T12:00:00Z").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

function HistoryItem({ item, index }) {
  const [open, setOpen] = useState(false);
  const plan = item.plan || {};
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.4) }}
      className="clay overflow-hidden"
      data-testid={`history-item-${item.date}`}
    >
      <button onClick={() => setOpen(!open)} data-testid={`history-toggle-${item.date}`}
        className="w-full flex items-center gap-4 p-5 text-left hover:bg-secondary/40 transition-colors">
        <div className="w-14 shrink-0 text-center">
          <div className="font-display text-lg font-medium leading-tight">{dateLabel(item.date).split(" ")[2] || ""}</div>
          <div className="text-[11px] text-muted2 uppercase">{dateLabel(item.date).split(",")[0]}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[15px] truncate">{plan.title || "Check-in only"}</div>
          <div className="flex items-center gap-3 text-xs text-muted2 mt-0.5">
            {plan.duration_min && <span className="inline-flex items-center gap-1"><Timer size={12} /> {plan.duration_min} min</span>}
            {plan.intensity && <span className="capitalize">{plan.intensity} intensity</span>}
            {plan.activity_type && <span className="capitalize">{plan.activity_type}</span>}
          </div>
        </div>
        <span className="font-display font-semibold text-brand tabular-nums" data-testid={`history-score-${item.date}`}>{item.score}</span>
        {item.feedback && FEEDBACK_BADGE[item.feedback] && (
          <span data-testid={`history-feedback-${item.date}`}
            className="hidden sm:inline-block text-[11px] font-medium rounded-full px-2.5 py-1 shrink-0"
            style={{ color: FEEDBACK_BADGE[item.feedback].color, background: FEEDBACK_BADGE[item.feedback].bg }}>
            {FEEDBACK_BADGE[item.feedback].label}
          </span>
        )}
        <CaretDown size={16} weight="bold" color="#7C8AA5" className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-border/60" data-testid={`history-detail-${item.date}`}>
          {item.insight && <p className="text-sm text-ink/80 italic leading-relaxed mt-3">"{item.insight}"</p>}
          {plan.steps?.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {plan.steps.map((s, i) => (
                <li key={i} className="text-sm text-muted2 flex gap-2"><span className="text-brand">•</span> {typeof s === "string" ? s : s?.text}</li>
              ))}
            </ul>
          )}
          {item.nutrition_headline && (
            <p className="text-xs text-muted2 mt-3"><span className="font-semibold text-ink/70">Nutrition:</span> {item.nutrition_headline}</p>
          )}
          <div className="flex flex-wrap gap-1.5 mt-4">
            {Object.entries(item.pillars || {}).map(([k, v]) => {
              const m = PILLAR_META[k];
              if (!m) return null;
              return (
                <span key={k} className="text-[11px] font-medium rounded-full px-2.5 py-1" style={{ background: `${m.color}18`, color: m.color }}>
                  {m.label} {v}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </motion.div>
  );
}
